import { useEffect, useRef } from 'react'

import type { ItemsWithPagination } from '@inno-sdks/files'
import { useQueryClient } from '@tanstack/react-query'
import { INNOSCRIPTA_INTERNAL_ORGANIZATION_ID } from 'shared/constants/user'
import { useUserContext } from 'shared/hooks/useUserContext'

import { initFilesSdk } from '../api/filesSdk'
import { useAppFolderId } from '../api/hooks/useAppFolderId'
import { FILES_LIST_QUERY_KEY } from '../api/hooks/useFilesList'
import { NEED_HELP_SHORTCUT } from '../constants/needHelpShortcut'
import { hasShortcutWithUrl } from '../utils/hasShortcutWithUrl'
import { provisionDesktopShortcut } from '../utils/provisionDesktopShortcut'

/** Provisions the pinned support entry independently from app shortcuts. */
export const useProvisionNeedHelpShortcut = (
  rootFolderItems?: ItemsWithPagination
) => {
  const { data: appFolderId } = useAppFolderId()
  const { user } = useUserContext()
  const queryClient = useQueryClient()
  const isProvisioningRef = useRef(false)

  useEffect(() => {
    if (
      appFolderId == null ||
      rootFolderItems == null ||
      user?.partner?.partner_type !== 'sff' ||
      user.organization_id === INNOSCRIPTA_INTERNAL_ORGANIZATION_ID ||
      isProvisioningRef.current ||
      hasShortcutWithUrl(rootFolderItems, NEED_HELP_SHORTCUT.shortcutUrl)
    )
      return

    const { filesSdk, success } = initFilesSdk()
    if (!success || !filesSdk) return

    isProvisioningRef.current = true

    const provision = async () => {
      const provisioned = await provisionDesktopShortcut({
        filesSdk,
        appFolderId,
        shortcutKey: NEED_HELP_SHORTCUT.key,
        name: NEED_HELP_SHORTCUT.name,
        shortcutUrl: NEED_HELP_SHORTCUT.shortcutUrl,
        failureEvent: 'desktop.need_help_shortcut_provision_failed'
      })
      isProvisioningRef.current = false
      if (provisioned === 'created') {
        queryClient.invalidateQueries({ queryKey: [FILES_LIST_QUERY_KEY] })
      }
    }

    provision()
  }, [
    appFolderId,
    queryClient,
    rootFolderItems,
    user?.organization_id,
    user?.partner?.partner_type
  ])
}
